/**
 * Tournament Configuration — `/configuration` (module 07).
 *
 * **What this screen is, and what it is not.** `07-configuration.md` describes eight
 * collapsible sections, five of which lock at go-live. The data layer exists for Section 06,
 * `Appointment Rules`, and for the one part of Section 04 that every other module reads —
 * **umpire report ownership** (§A5), which decides who drafts, marks and pushes each umpire's
 * report. The rest of Sections 01–05, 07 and 08 need either schema that does not exist yet or
 * a decision that has not been taken, so they are absent rather than mocked. A configuration
 * field that renders but stores nothing is worse than a missing one: it invites a TD to set a
 * value the tournament will never read.
 *
 * Four fetches, deliberately independent:
 *  · the section itself, which fails closed into the shared banner;
 *  · the change log, which is a separate route and refetched after every accepted write, so a
 *    change just made appears without a reload;
 *  · the roster, read only so a change-log actor id can carry a name. If it fails the log
 *    still renders, with ids as stored;
 *  · the ownership surface, which is its own route and its own panel.
 *
 * **TD-only, and the refusal is named.** Every configuration route is `requireTd`: a UM
 * credential is refused with a 403 on the reads as firmly as on the writes, because the
 * change log names people. A 401 is the session's business and is announced once, by the API
 * client, as the signed-out screen. A 403 is not — the credential is good and this surface is
 * simply not the holder's, which the page says plainly rather than showing an error the
 * reader would take for a fault.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FRAME IS THE MOCKUP'S, AND THE CONTENT IS WHATEVER IS REAL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `docs/design/configuration-page-v1.html` is the design authority, and it draws this module
 * as **one long page of eight numbered, collapsible section cards in setup order**, under a
 * `.cfgband` and a `Setup Progress` panel. An earlier build drew four flat panels instead,
 * which lost the two things the frame is for: the reader could not see how many sections
 * there are, and could not tell a section that is absent from one that had scrolled past.
 *
 * So the frame is restored in full — all eight cards, numbered, in the mockup's order, with
 * the mockup's sub-lines — and each card holds what this build actually has:
 *
 *  · **04 People** — the ownership surface, which is the one part of that section every other
 *    module reads.
 *  · **05 Regulations & Discipline Binding** — the Code of Conduct catalogue, which the
 *    hearings module quotes in every charge, letter and decision.
 *  · **06 Appointment Rules** — `Rules In Force` and `Tunable Thresholds`, whole.
 *  · **01, 02, 03, 07, 08** — an honest statement of what the section holds and why it is not
 *    built, in the mockup's own `.notebox`. Not an empty form: §07's own rule is that a
 *    configuration field which renders but stores nothing invites a TD to set a value the
 *    tournament will never read.
 *
 * **Nothing on this page is invented to fill the frame.** The mockup's `Complete` pills, its
 * `N / 10` checks, its `6` data sources and its `Tournament Is Live` banner are all
 * demonstration data; none of them has a route behind it, so none of them is drawn. The
 * status pills say what is true of each section in this build, and the figure strip prints an
 * em dash where there is no figure.
 *
 * **Which sections open by default is the one deliberate departure from §2.** The spec
 * expands 01, 02, 06 and 08. Here 01, 02 and 08 are empty statements and 04 and 05 hold real
 * controls, so the page opens **04, 05 and 06** — the three that carry work — and leaves the
 * five statements folded. `Expand All` reaches the rest in one press. Opening two empty cards
 * while folding the two that hold the tournament's live rule set would be the letter of the
 * spec against its purpose.
 */

import { useState } from "react";

import {
  ApiError,
  fetchCodeOfConduct,
  fetchConfigChangeLog,
  fetchConfiguration,
  fetchOfficials,
  fetchOwnership,
} from "../api/client.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useTournament } from "../app/session.js";
import { useAsync } from "../app/useAsync.js";
import { BrandingPanel } from "../features/configuration/BrandingPanel.js";
import { ChangeLogPanel } from "../features/configuration/ChangeLogPanel.js";
import { CodeOfConductPanel } from "../features/configuration/CodeOfConductPanel.js";
import { ConfigSection, ConfigSectionAbsent } from "../features/configuration/ConfigSection.js";
import { ConfigurationPanel } from "../features/configuration/ConfigurationPanel.js";
import { OwnershipPanel } from "../features/configuration/OwnershipPanel.js";
import { SetupProgressPanel } from "../features/configuration/SetupProgressPanel.js";

/** The eight sections, numbered and sub-lined exactly as `07-configuration.md` §2 lists them. */
const SECTION_NUMBERS = ["01", "02", "03", "04", "05", "06", "07", "08"] as const;

/** The three that carry live controls in this build — see the module note on §2's default. */
const OPEN_BY_DEFAULT: readonly string[] = ["04", "05", "06"];

function initialExpanded(): Record<string, boolean> {
  return Object.fromEntries(
    SECTION_NUMBERS.map((number) => [number, OPEN_BY_DEFAULT.includes(number)]),
  );
}

function allExpanded(value: boolean): Record<string, boolean> {
  return Object.fromEntries(SECTION_NUMBERS.map((number) => [number, value]));
}

export function ConfigurationPage(): React.JSX.Element {
  /** Bumped after every accepted write; the change log's only dependency. */
  const [logGeneration, setLogGeneration] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialExpanded);

  function toggle(number: string, next: boolean): void {
    setExpanded((current) => ({ ...current, [number]: next }));
  }

  /**
   * The tournament's own zone, for the change log's `When` column. Null while the session
   * boots and null when no tournament is set up; the panel falls back to the zone the rest of
   * this front end falls back to rather than being handed an empty string to format with.
   */
  const tournament = useTournament();

  const configuration = useAsync(() => fetchConfiguration(), []);
  const changeLog = useAsync(() => fetchConfigChangeLog(), [logGeneration]);
  const roster = useAsync(() => fetchOfficials(), []);
  /**
   * §A5 ownership. A fourth independent fetch, and independent for the same reason the change
   * log is a separate one: it is its own route, and a failure in it must not take the rule set
   * down with it. It is fetched ONCE — the panel re-renders from the server's reply to every
   * accepted assignment, so a refetch here would be a second, later answer to a question that
   * has already been answered.
   */
  const ownership = useAsync(() => fetchOwnership(), []);
  /**
   * §05's Code of Conduct catalogue. A fifth independent fetch, for the reason the ownership
   * one is independent: it is its own route, and a tournament that has bound no Code edition
   * must not take the rule set down with it. Fetched ONCE — the panel re-renders from the
   * server's reply to every accepted load, so a refetch here would be a second, later answer
   * to a question already answered.
   */
  const codeOfConduct = useAsync(() => fetchCodeOfConduct(), []);

  const forbidden =
    configuration.status === "error" &&
    configuration.error instanceof ApiError &&
    configuration.error.status === 403;

  if (forbidden) {
    return (
      <section className="panel panel--d1" style={{ marginTop: 22 }} aria-labelledby="cfgDenyTitle">
        <div className="panel__head">
          <h3 className="panel__title" id="cfgDenyTitle">
            Configuration Is The Technical Delegate&rsquo;s
          </h3>
          <span className="panel__note">Signed In · Not Permitted Here</span>
        </div>
        <div className="emptystate">
          <b>This Device Cannot Open Configuration</b>
          The rule set is the Technical Delegate&rsquo;s to set, and the change log names
          people, so every route behind this page is refused to an Umpires Manager
          credential — on the reads as firmly as on the writes. Nothing is wrong with this
          device&rsquo;s sign-in; it is still signed in and every other screen it is entitled
          to is unaffected.
        </div>
        <div className="panel__foot">{configuration.error.message}</div>
      </section>
    );
  }

  const rulesInForce =
    configuration.status === "ready"
      ? {
          inForce: configuration.data.rules.filter((rule) => rule.enabled).length,
          total: configuration.data.rules.length,
        }
      : null;

  return (
    <>
      {/*
        The mockup's `.cfgband`: the event's own name in the thin half — from the session's
        tournament branding, never a fixed string, because an installation with no tournament
        set up has no name to print — and the live summary on the right. §2's summary reads
        `8 sections · N of 10 checks passed · N rules in force`; the checks half is dropped
        rather than printed as `0 of 10`, because the pre-flight checklist is not built and a
        zero would say ten checks had been run and failed.
      */}
      <div className="cfgband">
        <h2>
          Tournament Config{" "}
          {tournament === null ? null : (
            <span className="md">· {tournament.fullNameForLetterheads}</span>
          )}
        </h2>
        <HowToUse id="configuration" title="How to use Tournament Config">
          <li>
            The page is <HowToLink to="setup-progress">eight numbered sections in setup
            order</HowToLink>. Press a section&rsquo;s title bar to open or fold it, or use
            Expand All to see the lot.
          </li>
          {/*
            These three steps anchor on the SECTION CARD, not on the panel inside it. A folded
            section unmounts its body, so a link straight to `rules-in-force` would find
            nothing and do nothing on a page the reader had tidied up. The card is always
            there, the pulse ring lands on it, and the control the step names is inside it.
          */}
          <li>
            <HowToLink to="section-06">Rules In Force</HowToLink>, in section 06, are the rules
            the drafting engine applies when it fills a match day. Change one and the next draft
            obeys it. <b>Tunable Thresholds</b>, below them, are the numbers those rules count
            against.
          </li>
          <li>
            There is no Save button on this page. Every control commits the moment you change
            it, and what you then see is the server&rsquo;s own reply, not a hopeful echo.
          </li>
          <li>
            <HowToLink to="section-05">Code Of Conduct Catalogue</HowToLink>, in section 05, is
            the offence list the disciplinary letters quote, bound to one named edition of the
            Code.
          </li>
          <li>
            <HowToLink to="section-04">Umpire Report Ownership</HowToLink>, in section 04, says
            which umpires manager owns each umpire&rsquo;s report.
          </li>
          <li>
            <HowToLink to="change-log">Change Log</HowToLink> records who changed what and when.
            Each row is written in the same act as the change it describes.
          </li>
          <li>
            A rule that proves too strict mid-tournament can be relaxed between match days.
            Nothing needs restarting.
          </li>
        </HowToUse>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="count" data-testid="cfg-summary">
          <b>8</b> sections
          {rulesInForce === null ? null : (
            <>
              <span className="sep" aria-hidden="true">
                ·
              </span>
              <b>{rulesInForce.inForce}</b> of <b>{rulesInForce.total}</b> rules in force
            </>
          )}
        </span>
      </div>

      <SetupProgressPanel
        rulesInForce={rulesInForce}
        onExpandAll={() => {
          setExpanded(allExpanded(true));
        }}
        onCollapseAll={() => {
          setExpanded(allExpanded(false));
        }}
      />

      <ConfigSection
        number="01"
        title="Event Identity & Branding"
        subtitle="Names, Dates, Emblem, Letterhead Marks"
        status={{ label: "Brand Assets Only", tone: "warn" }}
        expanded={expanded["01"] ?? false}
        onToggle={(next) => {
          toggle("01", next);
        }}
        bodyHoldsPanels
        foot={
          <>
            <b>Locks at go-live.</b> Changing the event name or dates mid-tournament would
            break TMS links, document headers and every stored citation.
          </>
        }
      >
        {/*
          §01's `Brand Assets` subhead is built; the rest of the section is not, and says so.
          The names and dates are already stored and are printed in the band at the top of this
          page — what is missing is an EDITOR for them, which is a different thing from a
          missing field. The letterhead mark slots are an ordered, addable, removable list whose
          composer has no route, so they are named as absent rather than drawn empty.
        */}
        <BrandingPanel />
        <ConfigSectionAbsent
          holds="the event’s names and dates, and the letterhead mark slots every generated document carries"
          reason="The tournament’s own name is already stored and is printed above, but nothing on this page edits it yet; the mark-slot list and the letterhead composer have no route behind them."
        />
      </ConfigSection>

      <ConfigSection
        number="02"
        title="Competitions & Data Sources"
        subtitle="TMS Ids, Fetch Validation, Per-Dataset Source"
        status={{ label: "Not On This Page Yet", tone: "muted" }}
        expanded={expanded["02"] ?? false}
        onToggle={(next) => {
          toggle("02", next);
        }}
        foot={
          <>
            <b>Locks at go-live, refresh still allowed.</b> The latest data can still be pulled
            from TMS, but the competition ids cannot be switched mid-tournament.
          </>
        }
      >
        <ConfigSectionAbsent
          holds="the competition ids this event runs, the validate-and-fetch control against TMS, and the per-dataset choice between a TMS fetch and an uploaded file"
          reason="The import screens are an open gap in the specification and no route accepts an uploaded dataset yet."
        />
      </ConfigSection>

      <ConfigSection
        number="03"
        title="Calendar, Venue & Time"
        subtitle="Matchdays, Rest Days, Pitches, Kick-Off Grids"
        status={{ label: "Not On This Page Yet", tone: "muted" }}
        expanded={expanded["03"] ?? false}
        onToggle={(next) => {
          toggle("03", next);
        }}
        foot={
          <>
            <b>Locks at go-live.</b> Fixture times are cited in hearing notices and decision
            letters.
          </>
        }
      >
        <ConfigSectionAbsent
          holds="the matchday count and numbering, the rest days, the venue and its pitches, and the kick-off grids that generated letters quote"
          reason="The tournament’s timezone is stored and every screen reads it; the rest of this section has no schema yet."
        />
      </ConfigSection>

      <ConfigSection
        number="04"
        title="People"
        subtitle="TD Signature Block, Role Taxonomy, Roster Source, App Access"
        status={{ label: "Report Ownership Only", tone: "warn" }}
        expanded={expanded["04"] ?? false}
        onToggle={(next) => {
          toggle("04", next);
        }}
        bodyHoldsPanels
        foot={
          <>
            <b>Ownership stays editable throughout.</b> The rest of this section locks at
            go-live, except per-official contact details, which stay editable on each profile.
          </>
        }
      >
        {ownership.status === "loading" ? (
          <section className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Umpire Report Ownership</h3>
              <span className="panel__note">Loading</span>
            </div>
            <Skeleton rows={6} />
          </section>
        ) : null}

        {ownership.status === "error" ? <FailBanner error={ownership.error} /> : null}

        {ownership.status === "ready" ? (
          <OwnershipPanel
            initialView={ownership.data}
            onWritten={() => {
              setLogGeneration((generation) => generation + 1);
            }}
          />
        ) : null}

        <div className="notebox" style={{ margin: "0 20px 18px" }}>
          <b>The rest of Section 04 is not on this page yet.</b> The Technical
          Delegate&rsquo;s signature block, the role taxonomy, the roster source summary and
          the app-access table each need schema or a decision this build does not have.
          Per-official contact overrides already live on each official&rsquo;s own profile,
          which is where they belong.
        </div>
      </ConfigSection>

      <ConfigSection
        number="05"
        title="Regulations & Discipline Binding"
        subtitle="Bound Code, Penalty Ladder, Referral Path, Appeals"
        status={{ label: "Code Of Conduct Only", tone: "warn" }}
        expanded={expanded["05"] ?? false}
        onToggle={(next) => {
          toggle("05", next);
        }}
        bodyHoldsPanels
        foot={
          <>
            <b>Locks at go-live.</b> Changing a bound instrument mid-tournament would
            invalidate decisions already served under the previous version.
          </>
        }
      >
        {codeOfConduct.status === "loading" ? (
          <section className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Code Of Conduct Catalogue</h3>
              <span className="panel__note">Loading</span>
            </div>
            <Skeleton rows={4} />
          </section>
        ) : null}

        {codeOfConduct.status === "error" ? <FailBanner error={codeOfConduct.error} /> : null}

        {codeOfConduct.status === "ready" ? (
          <CodeOfConductPanel initialView={codeOfConduct.data} />
        ) : null}

        <div className="notebox" style={{ margin: "0 20px 18px" }}>
          <b>The rest of Section 05 is not on this page yet.</b> Binding the edition itself,
          the citation string, the penalty ladder, the referral path and the appeal settings
          are absent, so an unbound tournament is told so on the panel above rather than being
          given an edition it never chose.
        </div>
      </ConfigSection>

      <ConfigSection
        number="06"
        title="Appointment Rules"
        subtitle="Ownership, Hard Blocks, Soft Warnings, Thresholds"
        status={{ label: "Stays Editable", tone: "accent" }}
        expanded={expanded["06"] ?? false}
        onToggle={(next) => {
          toggle("06", next);
        }}
        bodyHoldsPanels
        foot={
          <>
            <b>Stays editable throughout.</b> Every change is applied to future drafts only —
            appointments already published to TMS are left alone.
          </>
        }
      >
        {configuration.status === "loading" ? (
          <section className="panel">
            <div className="panel__head">
              <h3 className="panel__title">Rules In Force</h3>
              <span className="panel__note">Loading</span>
            </div>
            <Skeleton rows={6} />
          </section>
        ) : null}

        {configuration.status === "error" ? <FailBanner error={configuration.error} /> : null}

        {configuration.status === "ready" ? (
          <ConfigurationPanel
            initialView={configuration.data}
            onWritten={() => {
              setLogGeneration((generation) => generation + 1);
            }}
          />
        ) : null}
      </ConfigSection>

      <ConfigSection
        number="07"
        title="TD Preferences"
        subtitle="Lookahead, Alerts, Refresh, Marking, Exports"
        status={{ label: "Not On This Page Yet", tone: "muted" }}
        expanded={expanded["07"] ?? false}
        onToggle={(next) => {
          toggle("07", next);
        }}
        foot={
          <>
            <b>Stays editable throughout.</b> Preferences affect display and drafting only,
            never stored tournament data.
          </>
        }
      >
        <ConfigSectionAbsent
          holds="the Command Desk lookahead, the alert types, the refresh cadences, the marking category sets and the report and export defaults"
          reason="Each of these is read from a built-in default today, so a control here would let a preference be set that nothing reads."
        />
      </ConfigSection>

      <ConfigSection
        number="08"
        title="Verification & Go-Live"
        subtitle="Checklist, Source Status, Test Draft, Proceed"
        status={{ label: "Not On This Page Yet", tone: "muted" }}
        expanded={expanded["08"] ?? false}
        onToggle={(next) => {
          toggle("08", next);
        }}
        foot="Configuration stays reachable from the module navigation at any time; locked sections would be shown read-only with the reason for the lock."
      >
        <ConfigSectionAbsent
          holds="the pre-flight checklist, the tournament overview, the data-source status, the test appointment draft and the Proceed To Operations gate"
          reason="No route reports whether a tournament is live and nothing sets that state, so neither the checklist nor the gate can be drawn honestly."
        />
      </ConfigSection>

      <div style={{ marginTop: 22 }}>
        {changeLog.status === "loading" ? (
          <section className="panel panel--d2">
            <div className="panel__head">
              <h3 className="panel__title">Change Log</h3>
              <span className="panel__note">Loading</span>
            </div>
            <Skeleton rows={4} />
          </section>
        ) : null}

        {changeLog.status === "error" ? <FailBanner error={changeLog.error} /> : null}

        {changeLog.status === "ready" ? (
          <ChangeLogPanel
            entries={changeLog.data.entries}
            limit={changeLog.data.limit}
            rules={configuration.status === "ready" ? configuration.data.rules : []}
            officials={roster.status === "ready" ? roster.data.officials : []}
            {...(tournament === null ? {} : { timezone: tournament.timezone })}
          />
        ) : null}
      </div>

      <section className="panel panel--d3" style={{ marginTop: 22 }}>
        <div className="panel__head">
          <h3 className="panel__title">What Is Not On This Page</h3>
          <span className="panel__note">Absent, Not Hidden</span>
        </div>
        <div className="panel__foot" data-testid="config-scope-note">
          <b>Sections 01 to 05, 07 and 08 are mostly not on this page yet.</b> Identity, data
          sources, calendar, people, preferences and the go-live gate each need schema or a
          decision this build does not have. They are absent rather than drawn, because a
          configuration field that stores nothing is worse than one that is missing. Two parts
          ARE here, each because every other module reads it: umpire report ownership from
          Section 04, and the Code of Conduct catalogue from Section 05 &mdash; the offence
          list the hearings module quotes in every charge, every letter and every decision.
          The rest of Section 05 &mdash; binding the edition itself, the regulation citations
          and the appeal settings &mdash; is not, so an unbound tournament is told so on the
          panel above rather than being given an edition it never chose.
        </div>
      </section>
    </>
  );
}
